import {AsyncPipe} from '@angular/common';
import {Component, OnDestroy, OnInit, effect, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, NavigationEnd, Router, RouterModule} from '@angular/router';
import {ELoginStatus} from '@app/auth-config/models/auth-state.model';
import {AuthConfigService} from '@app/auth-config/services/auth-config.service';
import {selectTokenStatus} from '@app/auth-config/state/auth.selector';
import {StorageKeys} from '@app/core/constants/storage-keys';
import {FeatureFlagService} from '@app/core/services/feature-flag.service';
import {LocalStorageService} from '@app/core/services/local-storage.service';
import {ObjectUtils} from '@app/core/utils/object.utils';
import {ECustomerOrganizationStatus, ECustomerStatus} from '@app/shared/enums/header.enum';
import {ICustomerOrganization, IStatusState} from '@app/shared/models/header.model';
import {ILanguage} from '@app/shared/models/language.model';
import {PrimeNgModule} from '@app/shared/modules/prime-ng/prime-ng.module';
import {
  getCustomerProfile,
  loadCustomerOrganization,
  loadOrdersOnOrganizationChange,
  onChangeCustomerOrganization,
  setCustomerOrganization,
  setCustomerOrganizationStatus,
} from '@app/shared/state/header/header.action';
import {
  selectCustomerOrganization,
  selectCustomerOrganizationAndStatus,
  selectCustomerOrganizationStatus,
  selectCustomerRole,
  selectOrganizations,
  selectUserName,
} from '@app/shared/state/header/header.selector';
import {selectActiveLanguages} from '@app/shared/state/language/language.selector';
import {environment} from '@environments/environment';
import {Store} from '@ngrx/store';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {MenuItem} from 'primeng/api';
import {MenuModule} from 'primeng/menu';
import {SelectChangeEvent, SelectModule} from 'primeng/select';
import {SplitButtonModule} from 'primeng/splitbutton';
import {TabsModule} from 'primeng/tabs';
import {Observable, Subject, takeUntil, tap} from 'rxjs';
import {AlgoliaSearchComponent} from '../algolia-search/algolia-search.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    TranslateModule,
    RouterModule,
    TabsModule,
    SelectModule,
    MenuModule,
    SplitButtonModule,
    FormsModule,
    PrimeNgModule,
    AlgoliaSearchComponent,
    AsyncPipe,
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly route = inject(ActivatedRoute);
  private readonly authConfigService = inject(AuthConfigService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly featureFlagService = inject(FeatureFlagService);

  accountMenuItems: MenuItem[];
  activeMenuItemIndex!: number;
  profileMenu: MenuItem[] = [];
  userName$: Observable<string | null>;
  firstIndex = 0;
  organizations = [] as ICustomerOrganization[];
  customerStatus: IStatusState | null = null;
  organizations$: Observable<ICustomerOrganization[] | null>;
  currentUrl: string = '';
  myOrderNavigationUrl = '/my-orders';
  selectedOrganization!: number | null | undefined;
  selectedCountryId!: number | null | undefined;
  loginState!: ELoginStatus;
  ELoginStatus = ELoginStatus;
  activeLanguages: ILanguage[] = [];
  private readonly unsubscribe$ = new Subject<void>();
  private managerRoleId = Number(environment.managerRoleId);
  private hasRequestedCustomerProfile = false;
  private isManager = false;
  security2FAFlag = '';

  header = {
    profile_Settings: 'Header.Profile_Settings',
    logout: 'Header.Logout',
    profile: '/auth/profile',
    securitySettings: '/auth/security-settings',
    securitySettingsLabel: 'Header.Security_Settings',
  };
  isActiveAccount = false;
  constructor() {
    this.security2FAFlag = environment.featureFlagKeys.security2FAFlag?.name ?? '';
    this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe((event: any) => {
      this.handleRouteEvents(event);
    });

    this.accountMenuItems = [];

    this.translate.get([this.header.profile_Settings, this.header.logout]).subscribe(translation => {
      this.profileMenu = [
        {
          label: translation[this.header.profile_Settings],
          icon: 'pi pi-user-edit',
          command: () => {
            this.router.navigate([this.header.profile]);
          },
        },
        {
          label: translation[this.header.logout],
          icon: 'pi pi-upload',
          command: () => this.onLogoutClicked(),
        },
      ];
    });

    this.organizations$ = this.store.select(selectOrganizations);
    this.userName$ = this.store.select(selectUserName);
    this.store
      .select(selectActiveLanguages)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(languages => {
          this.activeLanguages = languages ?? [];
        })
      )
      .subscribe();

    effect(() => {
      const flagValue = this.featureFlagService.isFlagEnabled(this.security2FAFlag)();
      this.profileMenu = [
        {
          label: this.translate.instant(this.header.profile_Settings),
          icon: 'pi pi-user-edit',
          command: () => {
            this.router.navigate([this.header.profile]);
          },
        },
        ...(flagValue
          ? [
              {
                label: this.translate.instant(this.header.securitySettingsLabel),
                icon: 'pi pi-shield',
                command: () => {
                  this.router.navigate([this.header.securitySettings]);
                },
              },
            ]
          : []),
        {
          label: this.translate.instant(this.header.logout),
          icon: 'pi pi-upload',
          command: () => this.onLogoutClicked(),
        },
      ];
    });
  }

  private handleRouteEvents(event: any) {
    const innerNavEvent = event.routerEvent instanceof NavigationEnd ? event.routerEvent : null;
    const postNavEvent = event instanceof NavigationEnd ? event : innerNavEvent;
    if (postNavEvent) {
      this.currentUrl = postNavEvent.url;
      this.selectActiveMenu();
      this.loadProfileAfterCallbackNavigation();
    }
  }

  private loadProfileAfterCallbackNavigation(): void {
    if (
      this.loginState === ELoginStatus.LoggedIn &&
      !this.hasRequestedCustomerProfile &&
      !this.isAuthCallbackRoute(this.currentUrl) &&
      this.authConfigService.isTokenValid()
    ) {
      this.hasRequestedCustomerProfile = true;
      this.getCustomerProfile();
      this.getCustomerDefaultOrganizationAndAccountStatus();
    }
  }

  private isAuthCallbackRoute(url: string): boolean {
    return url.includes('/auth/callback');
  }

  private selectActiveMenu() {
    const ordersMenu = ['/my-orders', '/checkout', '/order-details'];
    const testsMenu = ['/category', '/product'];
    const supportMenu = ['/static-pages', '/support'];
    const accountsMenu = ['/accounts', '/add-account', '/edit-account'];
    let mainMenuUrl;
    if (ordersMenu.some(m => this.currentUrl.includes(m))) {
      mainMenuUrl = ordersMenu[0];
    } else if (testsMenu.some(m => this.currentUrl.includes(m))) {
      mainMenuUrl = testsMenu[0];
    } else if (supportMenu.some(m => this.currentUrl.includes(m))) {
      mainMenuUrl = supportMenu[0];
    } else if (accountsMenu.some(m => this.currentUrl.includes(m))) {
      mainMenuUrl = accountsMenu[0];
    } else {
      mainMenuUrl = this.currentUrl;
    }
    const activeMenuItem = this.accountMenuItems.find(item => {
      return item.routerLink.includes(mainMenuUrl);
    });
    this.activeMenuItemIndex = this.accountMenuItems.findIndex(x => x.routerLink === activeMenuItem?.routerLink);
  }

  private onLogoutClicked() {
    this.oidcSecurityService.logoffAndRevokeTokens().subscribe(() => {
      type StorageKey = keyof typeof StorageKeys;
      (Object.keys(StorageKeys) as StorageKey[]).forEach(key => {
        this.localStorageService.removeItem(StorageKeys[key]);
      });
      this.activeLanguages.forEach(language => {
        this.localStorageService.removeItem(`LocalizedProperties_${language.id}`);
      });
    });
  }

  ngOnInit(): void {
    this.getCustomerProfileAndOrganizationAndAccountStatusOnValidToken();
    this.initCustOrgStatus();
    this.initManagerHeader();
    this.syncCurrentRoute();
  }

  private syncCurrentRoute(): void {
    if (!this.currentUrl) {
      this.currentUrl = this.router.url;
      this.selectActiveMenu();
    }
  }

  getCustomerProfileAndOrganizationAndAccountStatusOnValidToken() {
    this.store
      .select(selectTokenStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(state => {
          if (state == ELoginStatus.LoggedIn && this.authConfigService.isTokenValid()) {
            this.loginState = ELoginStatus.LoggedIn;
            if (this.isAuthCallbackRoute(this.currentUrl || this.router.url)) {
              return;
            }
            if (!this.hasRequestedCustomerProfile) {
              this.hasRequestedCustomerProfile = true;
              this.getCustomerProfile();
            }
            this.getCustomerDefaultOrganizationAndAccountStatus();
          }
          if (
            (state == ELoginStatus.Authorized || state == ELoginStatus.Initial) &&
            this.authConfigService.isTokenValid()
          ) {
            this.loginState = ELoginStatus.LoggedOut;
            this.hasRequestedCustomerProfile = false;
          }
        })
      )
      .subscribe();

    this.store
      .select(selectCustomerOrganization)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((state: ICustomerOrganization | null) => {
          this.selectedOrganization = state?.organizationId;
          this.selectedCountryId = state?.organization?.countryId;
          const organizationId = this.localStorageService.getItem(StorageKeys.selectedOrganization);
          if (!organizationId && this.selectedOrganization) {
            this.localStorageService.setItem(StorageKeys.selectedOrganization, this.selectedOrganization);
          }
        })
      )
      .subscribe();
  }

  onChangeOrganization(event: SelectChangeEvent): void {
    const filteredOrganization = this.organizations.find(organization => organization.organizationId === event.value);
    if (filteredOrganization) {
      this.dispatchCustomerOrganizationAndAccountStatus(filteredOrganization, this.customerStatus, true);
      if (this.currentUrl === this.myOrderNavigationUrl) {
        this.store.dispatch(loadOrdersOnOrganizationChange({isOrganizationChanged: true}));
      }
    }
    this.navigateToOrdersPage();
  }

  private getCustomerProfile(): void {
    this.store.dispatch(getCustomerProfile({useCache: true}));
  }

  private getCustomerDefaultOrganizationAndAccountStatus(): void {
    this.store
      .select(selectCustomerOrganizationAndStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(CustomerInfo => {
          if (CustomerInfo?.customerOrganizations) {
            this.customerStatus = CustomerInfo.customerStatus;
            this.setCustomerOrganizationAndAccountStatus(CustomerInfo.customerOrganizations, this.customerStatus);
          }
        })
      )
      .subscribe();
  }

  private setCustomerOrganizationAndAccountStatus(
    organizations: ICustomerOrganization[] | null,
    customerStatus: IStatusState | null
  ): void {
    if (organizations) {
      this.organizations = organizations;

      const filteredOrganization =
        this.organizations.find(x => x.organizationId == this.selectedOrganization) ?? organizations[this.firstIndex];
      this.selectedOrganization = filteredOrganization.organizationId;
      this.selectedCountryId = filteredOrganization.organization?.countryId;
      if (filteredOrganization) {
        this.dispatchCustomerOrganizationAndAccountStatus(filteredOrganization, customerStatus);
      }
    }
  }

  private dispatchCustomerOrganizationAndAccountStatus(
    filteredOrganization: ICustomerOrganization,
    customerStatus: IStatusState | null,
    isDropdownChangeEvent?: boolean
  ): void {
    const accountStatus = this.getCustomerOrganizationStatus(filteredOrganization, customerStatus);

    if (!isDropdownChangeEvent) {
      this.store.dispatch(
        setCustomerOrganization({
          organization: filteredOrganization,
        })
      );
    } else {
      this.store.dispatch(
        onChangeCustomerOrganization({
          organization: filteredOrganization,
        })
      );
    }

    this.store.dispatch(
      setCustomerOrganizationStatus({
        accountStatus: accountStatus,
      })
    );

    this.store.dispatch(loadCustomerOrganization());
  }

  private getCustomerOrganizationStatus(
    filteredOrganization: ICustomerOrganization,
    customerStatus: IStatusState | null
  ): ECustomerOrganizationStatus {
    if (
      !ObjectUtils.isEmpty(filteredOrganization) &&
      filteredOrganization?.customerOrganizationStatus.id === ECustomerOrganizationStatus.Active &&
      filteredOrganization?.organization.organizationStatus?.id === ECustomerOrganizationStatus.Active &&
      customerStatus?.id === ECustomerStatus.Verified
    ) {
      return ECustomerOrganizationStatus.Active;
    }

    return ECustomerOrganizationStatus.New;
  }

  private navigateToOrdersPage(): void {
    if (this.route.snapshot.queryParams['id'] && this.router.url.includes('checkout')) {
      this.router.navigate([this.myOrderNavigationUrl]);
    }
  }

  login() {
    this.oidcSecurityService.authorize();
  }

  private initManagerHeader() {
    this.store
      .select(selectCustomerRole)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(customerRoleId => {
          this.isManager = customerRoleId === this.managerRoleId;
          if (this.isManager) {
            this.updateHeaderMenuForManager();
            this.initManagerProfileMenu();
          }
        })
      )
      .subscribe();
  }

  private updateHeaderMenuForManager() {
    if (!this.accountMenuItems.some(item => item.routerLink === '/accounts')) {
      this.accountMenuItems.push({
        id: 'MenuItem.Item4',
        routerLink: '/accounts',
        label: this.translate.instant('Header.Accounts'),
      });
    }

    this.selectActiveMenu();
  }

  private initManagerProfileMenu() {
    this.profileMenu = [
      {
        label: this.translate.instant(this.header.profile_Settings),
        icon: 'pi pi-user-edit',
        command: () => {
          this.router.navigate([this.header.profile]);
        },
      },
      {
        label: this.translate.instant('Header.Institution_Settings'),
        icon: 'pi pi-building',
        command: () => {
          this.router.navigate(['/auth/institution-settings']);
        },
      },
      {
        label: this.translate.instant(this.header.logout),
        icon: 'pi pi-upload',
        command: () => {
          this.onLogoutClicked();
        },
      },
    ];
  }

  private initCustOrgStatus(): void {
    this.store
      .select(selectCustomerOrganizationStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(accStatus => {
          this.isActiveAccount = accStatus === ECustomerOrganizationStatus.Active;
          if (this.isActiveAccount) this.updateHeaderMenuForActiveAccount();
        })
      )
      .subscribe();
  }

  private updateHeaderMenuForActiveAccount() {
    this.accountMenuItems = [];
    this.accountMenuItems.unshift({
      id: 'MenuItem.Item2',
      routerLink: '/category',
      label: this.translate.instant('Header.Tests'),
    });
    this.accountMenuItems.unshift({
      id: 'MenuItem.Item1',
      routerLink: this.myOrderNavigationUrl,
      label: this.translate.instant('Header.Orders'),
    });
    if (this.isManager) {
      this.updateHeaderMenuForManager();
      return;
    }
    this.selectActiveMenu();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
